import type { AccessLabels } from '@kizen/permissions/types';
import type { PermissionRangeInputProps } from './PermissionRangeInput';

import { css } from '@emotion/core';
import styled from '@emotion/styled';
import { useIsPermissionVisible } from '@kizen/permissions/hooks/useIsPermissionVisible';
import { getStreamKey } from '@kizen/permissions/utils';
import { KizenTypography, truncateCss } from '__app/typography';
import { PermissionRangeInput } from './PermissionRangeInput';
import { GridContainer, HeaderToggles } from './styled';
import { Tooltip } from './Tooltip';

type PermissionLabelProps = {
  label: string;
  labelIndent?: number;
  tooltip?: string;
};

interface CategoryLabelProps extends PermissionLabelProps {
  accessLabels?: AccessLabels;
  marginBottom?: number | string | null;
}

type PermissionRowProps = {
  affordance: PermissionRangeInputProps['affordance'];
  section?: string;
  permission: string;
  label: string;
  tooltip?: string;
  labelIndent?: number;
  disabledSteps?: number[];
};

const PermissionInputContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  margin-bottom: 15px;
`;

const Label = styled(KizenTypography)`
  ${truncateCss}
  margin-bottom: 15px;
`;

const LabelContainer = styled.div<{ indent?: number }>`
  display: flex;
  align-items: flex-start;
  gap: 5px;
  ${({ indent }) =>
    indent &&
    css`
      padding-left: ${indent}px;
    `}
`;

export const PermissionLabel = ({
  label,
  labelIndent,
  tooltip,
}: PermissionLabelProps) => (
  <LabelContainer indent={labelIndent}>
    <Label>{label}</Label>
    <Tooltip text={tooltip} show={Boolean(tooltip)} />
  </LabelContainer>
);

export const CategoryLabel = ({
  accessLabels,
  label,
  labelIndent,
  tooltip,
  marginBottom,
}: CategoryLabelProps) => {
  return (
    <GridContainer marginBottom={marginBottom}>
      <LabelContainer indent={labelIndent}>
        <Label>{label}</Label>
        <Tooltip text={tooltip} show={Boolean(tooltip)} />
      </LabelContainer>
      {accessLabels && (
        <HeaderToggles>
          <KizenTypography as="span" weight="bold">
            {accessLabels[0]}
          </KizenTypography>
          <KizenTypography as="span" weight="bold">
            {accessLabels[1]}
          </KizenTypography>
          <KizenTypography as="span" weight="bold">
            {accessLabels[2]}
          </KizenTypography>
          <KizenTypography as="span" weight="bold">
            {accessLabels[3]}
          </KizenTypography>
        </HeaderToggles>
      )}
    </GridContainer>
  );
};

export const PermissionRow = (props: PermissionRowProps) => {
  const {
    section,
    permission,
    affordance,
    label,
    tooltip,
    labelIndent = 0,
    disabledSteps = [],
  } = props;

  const key = getStreamKey(permission, section);
  const isVisible = useIsPermissionVisible(key);

  if (!isVisible) {
    return null;
  }

  return (
    <GridContainer>
      <PermissionLabel
        label={label}
        tooltip={tooltip}
        labelIndent={labelIndent}
      />
      <PermissionInputContainer>
        <PermissionRangeInput
          permissionKey={key}
          affordance={affordance}
          disabledSteps={disabledSteps}
        />
      </PermissionInputContainer>
    </GridContainer>
  );
};
